/**
 * Spacing Box Model Component
 * Visual CSS box model diagram for editing margin and padding per-side
 */

import React from 'react';

interface SpacingValues {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
}

interface SpacingBoxModelProps {
  margin: SpacingValues;
  padding: SpacingValues;
  onMarginChange: (side: keyof SpacingValues, value: string) => void;
  onPaddingChange: (side: keyof SpacingValues, value: string) => void;
}

function SpacingInput({
  value,
  onChange,
  position,
}: {
  value: string;
  onChange: (v: string) => void;
  position: string;
}) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="0"
      className={`w-10 h-5 text-center text-[10px] font-mono bg-transparent border-0 focus:outline-none focus:bg-white/80 focus:ring-1 focus:ring-blue-400 rounded ${position}`}
    />
  );
}

export function SpacingBoxModel({
  margin,
  padding,
  onMarginChange,
  onPaddingChange,
}: SpacingBoxModelProps) {
  return (
    <div className="select-none">
      {/* Margin layer (outermost) */}
      <div className="relative bg-orange-50 border border-orange-200 rounded p-1">
        <span className="absolute top-0.5 left-1.5 text-[9px] text-orange-400 font-medium">margin</span>

        {/* Margin top */}
        <div className="flex justify-center py-0.5">
          <SpacingInput
            value={margin.top || ''}
            onChange={(v) => onMarginChange('top', v)}
            position=""
          />
        </div>

        <div className="flex items-center">
          {/* Margin left */}
          <div className="flex-shrink-0 px-0.5">
            <SpacingInput
              value={margin.left || ''}
              onChange={(v) => onMarginChange('left', v)}
              position=""
            />
          </div>

          {/* Padding layer (inner) */}
          <div className="flex-1 relative bg-green-50 border border-green-200 rounded p-1">
            <span className="absolute top-0.5 left-1.5 text-[9px] text-green-500 font-medium">padding</span>

            {/* Padding top */}
            <div className="flex justify-center py-0.5">
              <SpacingInput
                value={padding.top || ''}
                onChange={(v) => onPaddingChange('top', v)}
                position=""
              />
            </div>

            <div className="flex items-center">
              {/* Padding left */}
              <div className="flex-shrink-0 px-0.5">
                <SpacingInput
                  value={padding.left || ''}
                  onChange={(v) => onPaddingChange('left', v)}
                  position=""
                />
              </div>

              {/* Content box (center) */}
              <div className="flex-1 bg-blue-50 border border-blue-200 rounded text-center py-2">
                <span className="text-[9px] text-blue-400">content</span>
              </div>

              {/* Padding right */}
              <div className="flex-shrink-0 px-0.5">
                <SpacingInput
                  value={padding.right || ''}
                  onChange={(v) => onPaddingChange('right', v)}
                  position=""
                />
              </div>
            </div>

            {/* Padding bottom */}
            <div className="flex justify-center py-0.5">
              <SpacingInput
                value={padding.bottom || ''}
                onChange={(v) => onPaddingChange('bottom', v)}
                position=""
              />
            </div>
          </div>

          {/* Margin right */}
          <div className="flex-shrink-0 px-0.5">
            <SpacingInput
              value={margin.right || ''}
              onChange={(v) => onMarginChange('right', v)}
              position=""
            />
          </div>
        </div>

        {/* Margin bottom */}
        <div className="flex justify-center py-0.5">
          <SpacingInput
            value={margin.bottom || ''}
            onChange={(v) => onMarginChange('bottom', v)}
            position=""
          />
        </div>
      </div>
    </div>
  );
}
